import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type {
  MerchantSettings,
  MerchantSettingsUpdate,
  ApiResponse,
  SizeGuideCategories,
  SizeGuideEntryView,
} from '../types';

/**
 * Which size guide mutation (if any) is in flight, and for which entry. A
 * create has no id yet — the guide does not exist until the response comes
 * back — patch and delete always target one.
 */
export interface SizeGuideBusyState {
  op: 'create' | 'patch' | 'delete';
  id?: string;
}

/**
 * The backend's error code for the last failed size guide mutation, the
 * name of the guide it collided with when the code is `category_conflict`
 * or `duplicate_fallback`, and the guide the failure happened to. `id` is
 * undefined only for a failed create, which has no guide yet to attach to —
 * the section renders that case at the section level rather than on a card.
 * The code itself is never interpreted here — turning it into copy a
 * merchant can act on is the section's job, not this hook's — but `id` has
 * to live here: `sizeGuideBusy`/`sizeGuideError` are each a single slot, so
 * two guides can have mutations in flight at once, and only this hook knows
 * which guide a given response actually belongs to.
 */
export interface SizeGuideErrorState {
  code: string;
  conflictName?: string;
  correlationId?: string;
  id?: string;
}

/**
 * ApiResponse plus the optional `conflictName` a size guide mutation's error
 * body may carry (routes/settings.ts spreads the SizeGuideMutation error
 * variant onto the JSON response, so it rides alongside `error`).
 */
type SizeGuideResponse<T> = ApiResponse<T> & { conflictName?: string; correlationId?: string };

/**
 * Sequence-number key for a create, which has no guide id to key requests
 * by. Two concurrent creates cannot actually reach the network today (the
 * drop zone that starts one disables itself for the duration), but a create
 * can still race a patch or delete on an EXISTING guide, so it needs the
 * same staleness protection those get.
 *
 * A plain word is enough to keep this out of a real guide id's namespace —
 * no exotic byte required. Guide ids are 24-character lowercase hex
 * ObjectId strings (SizeGuideEntry.id, web/backend/src/models/
 * merchantSettings.ts), so 'create' fails to collide on both length (6
 * characters, not 24) and alphabet ('create' contains 'r' and 't', neither
 * of which is a hex digit).
 */
const CREATE_KEY = 'create';

/**
 * Symbolic key for the coverage poll's own ticket — same `requestSeq`
 * namespace as `CREATE_KEY` and real guide ids, but not tied to any one
 * guide: refreshShopSizeCoverage (routes/settings.ts) refreshes ONE
 * shop-wide report regardless of which guide's mutation triggered it, so
 * ANY new size-guide mutation — not just one for the same guide — must
 * supersede a poll already in flight, or two polls could apply their
 * answers out of order. See pollForFreshCoverage.
 */
const COVERAGE_POLL_KEY = 'coverage-poll';

/** How long to wait between each background check for a fresher coverage
 * report after a size guide mutation, and how many times to check before
 * giving up quietly. refreshShopSizeCoverage (routes/settings.ts) is
 * fire-and-forget and can involve dozens of sequential Shopify GraphQL
 * calls (see its own MAX_SAMPLE_REQUESTS on the backend), so it is
 * realistically still running when the mutation that triggered it has
 * already resolved on this side — an immediate refetch would just read back
 * the same pre-mutation report. 5 attempts, 3s apart, is comfortably over
 * the ~15 sequential calls a typical shop's refresh needs, without holding
 * a poll open indefinitely for a shop whose refresh is genuinely stuck
 * (that shop still has SizeGuidesSection's `checkedAt` label, which this
 * mechanism exists to make trustworthy either way). See pollForFreshCoverage. */
const COVERAGE_POLL_INTERVAL_MS = 3000;
const COVERAGE_POLL_ATTEMPTS = 5;

/**
 * Hook to fetch and update merchant settings
 */
export function useSettings() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sizeGuideBusy, setSizeGuideBusy] = useState<SizeGuideBusyState | null>(null);
  const [sizeGuideError, setSizeGuideError] = useState<SizeGuideErrorState | null>(null);

  /** Mirrors `settings` outside of React's state, so a mutation can read the
   * value current AT THE MOMENT it starts without adding `settings` to its
   * own useCallback deps (which would recreate create/update/deleteSizeGuide
   * on every fetch — harmless, but needless). Used only to snapshot
   * `sizeCoverageReport.checkedAt` before a mutation, for pollForFreshCoverage
   * below. */
  const settingsRef = useRef<MerchantSettings | null>(null);
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  /**
   * Latest sequence number issued per key (a guide id, or CREATE_KEY),
   * bumped every time a mutation for that key starts. `sizeGuideBusy` and
   * `sizeGuideError` are each a single slot shared across every guide, so
   * two mutations for two DIFFERENT guides can be in flight together and
   * whichever started most recently owns the slot for display. This ref is
   * what lets an in-flight request recognise, once its response finally
   * arrives, whether it has since been superseded by a NEWER request for
   * the SAME key — a different guide's mutation starting never invalidates
   * this one, only another request for this exact key does.
   */
  const requestSeq = useRef<Record<string, number>>({});

  /** Call at the START of a mutation for `key`, before the request goes
   * out. The returned number is this attempt's ticket. */
  const beginRequest = useCallback((key: string): number => {
    const seq = (requestSeq.current[key] ?? 0) + 1;
    requestSeq.current[key] = seq;
    return seq;
  }, []);

  /** True while `seq` is still the most recent ticket issued for `key` —
   * false once a newer request for that same key has started, which means
   * THIS response is stale and must not touch shared state. */
  const isLatestRequest = useCallback(
    (key: string, seq: number): boolean => requestSeq.current[key] === seq,
    []
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/settings');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<MerchantSettings> = await response.json();

      if (json.success && json.data) {
        setSettings(json.data);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch settings';
      setError(message);
      console.error('Settings fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  /**
   * After a size guide mutation, the coverage report the merchant is
   * looking at describes the assignment BEFORE the change, not the one they
   * just saved — the backend's refresh is fire-and-forget, for exactly the
   * reason createSizeGuide/updateSizeGuide/deleteSizeGuide's own docs give:
   * sampling can take many sequential Shopify calls, too slow to hold a
   * save open for. Rather than one likely-premature refetch, this polls a
   * few times, adopting the first response whose `sizeCoverageReport.
   * checkedAt` differs from what the merchant was looking at when the
   * mutation started (`baselineCheckedAt`). Giving up after
   * COVERAGE_POLL_ATTEMPTS leaves the last-known report in place — still
   * visibly dated (SizeGuidesSection renders `checkedAt`), which is the
   * fallback this mechanism exists to make truthful either way.
   *
   * Deliberately bypasses `fetchData` (rather than calling it and reading
   * `settings` back afterwards): `fetchData` also toggles `loading`, which
   * would flash the whole settings page into its skeleton state on every
   * poll tick, for a refresh nothing on screen asked for. This does the
   * same GET, silently, and only touches `settings` on an actual change —
   * errors are swallowed for the same reason: a poll racing a mutation the
   * merchant has already been told succeeded must never surface a fetch
   * hiccup as if the save itself had failed.
   *
   * Two more races this needs to survive, on top of that one — both
   * previously real, both caught by review before shipping:
   *
   * - EVERY mutation starts its own independent poll (createSizeGuide,
   *   updateSizeGuide, deleteSizeGuide all call this). Nothing stops a
   *   second mutation on the same guide — routine, since SizeGuideCard
   *   fires an unbatched PATCH per product-type/tag add or remove — from
   *   succeeding while an earlier mutation's poll is still out. If that
   *   earlier poll's GET is then merely slow (ordinary network variance,
   *   no failure), its answer describes server state from BEFORE the
   *   second mutation and must not be allowed to win just because it
   *   happens to arrive last. Guarded with the SAME `requestSeq`/
   *   `beginRequest`/`isLatestRequest` ticketing the mutations already use
   *   for their own busy/error state — not a second mechanism — keyed by
   *   COVERAGE_POLL_KEY (not a guide id: this report is shop-wide, so a
   *   NEW mutation on ANY guide must supersede a poll already in flight,
   *   the same way a newer mutation on the same guide already does for
   *   `sizeGuideBusy`/`sizeGuideError`).
   * - The response this poll adopts must never become a whole-document
   *   write. This function's only job is refreshing `sizeCoverageReport`;
   *   merging the rest of a (possibly stale, possibly just-superseded)
   *   response into `settings` risks reverting an unrelated edit —
   *   another guide's rename, a `targeting` or `rateLimiting` change saved
   *   through `updateSettings` — that landed after this poll's snapshot
   *   was taken. So only `sizeCoverageReport` is ever merged in, never the
   *   full `json.data`.
   */
  const pollForFreshCoverage = useCallback(
    async (baselineCheckedAt: string | null) => {
      const seq = beginRequest(COVERAGE_POLL_KEY);
      for (let attempt = 0; attempt < COVERAGE_POLL_ATTEMPTS; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, COVERAGE_POLL_INTERVAL_MS));
        try {
          const response = await authenticatedFetch('/api/settings');
          if (!response.ok) continue;
          const json: ApiResponse<MerchantSettings> = await response.json();
          if (!json.success || !json.data) continue;
          // A newer mutation's poll (any guide — see COVERAGE_POLL_KEY) has
          // already started: this response is no longer the one that gets
          // to answer, whatever it says, and there is no point retrying
          // further — the newer poll already owns this job.
          if (!isLatestRequest(COVERAGE_POLL_KEY, seq)) return;
          const freshReport = json.data.sizeCoverageReport;
          const freshCheckedAt = freshReport?.checkedAt ?? null;
          if (freshCheckedAt !== baselineCheckedAt) {
            // Merge only the coverage report. Never `setSettings(json.data)`
            // — see this function's own doc comment.
            setSettings((current) => (current ? { ...current, sizeCoverageReport: freshReport } : current));
            return;
          }
        } catch (err) {
          console.error('Coverage poll error:', err);
          // One failed poll is not worth giving up the whole window for.
        }
      }
    },
    [authenticatedFetch, beginRequest, isLatestRequest]
  );

  const updateSettings = useCallback(async (updates: MerchantSettingsUpdate): Promise<boolean> => {
    setSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || `HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<MerchantSettings> = await response.json();

      if (!json.success) {
        throw new Error(json.error || 'Unknown API error');
      }

      // The PUT returns the whole settings shape, not an echo of the body, so
      // this cannot blank the fields the request did not carry. It omits `data`
      // entirely when the write committed but could not be read back: the save
      // still succeeded, and keeping the state we already hold is the only
      // honest answer, since adopting nothing would blank the form.
      if (json.data) {
        setSettings(json.data);
      }
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update settings';
      setError(message);
      console.error('Settings update error:', err);
      return false;
    } finally {
      setSaving(false);
    }
  }, [authenticatedFetch]);

  /**
   * POST a new guide as multipart under the field name the backend reads
   * (`guide`), with an optional display `name`. The response body IS the
   * created entry (routes/settings.ts POST /size-guides) — appended to local
   * state directly, never a refetch.
   *
   * Resolves false and leaves `sizeGuideError` holding the backend's code
   * (`unreadable_guide`, `too_many_guides`, ...) so the section can turn it
   * into copy a merchant can act on. Nothing is written to `settings` on a
   * failure — whatever guides were already there still are.
   *
   * Every write to the shared `sizeGuideBusy`/`sizeGuideError` slots is
   * guarded by `isLatestRequest`: a slower create that a newer create (or a
   * patch/delete elsewhere) has since superseded must not clobber state
   * that has already moved on. See `requestSeq`'s doc comment.
   */
  const createSizeGuide = useCallback(
    async (file: File, name?: string): Promise<boolean> => {
      const seq = beginRequest(CREATE_KEY);
      setSizeGuideBusy({ op: 'create' });
      // Only a PREVIOUS create's error is this attempt's business — an
      // error on some guide's card is unrelated and must stay visible.
      setSizeGuideError((current) => (current?.id === undefined ? null : current));
      // Snapshot BEFORE the request goes out: what the merchant is looking
      // at right now, the baseline pollForFreshCoverage waits to move past.
      const coverageBaseline = settingsRef.current?.sizeCoverageReport?.checkedAt ?? null;

      try {
        const body = new FormData();
        body.append('guide', file);
        if (name !== undefined) body.append('name', name);

        const response = await authenticatedFetch('/api/settings/size-guides', {
          method: 'POST',
          body,
        });
        const json: SizeGuideResponse<SizeGuideEntryView> = await response.json();

        if (!response.ok || !json.success || !json.data) {
          if (isLatestRequest(CREATE_KEY, seq)) {
            setSizeGuideError({ code: json.error || `http_${response.status}`, conflictName: json.conflictName, correlationId: json.correlationId });
          }
          return false;
        }

        const created = json.data;
        setSettings((current) =>
          current ? { ...current, sizeGuides: [...current.sizeGuides, created] } : current
        );
        // Fire-and-forget: the backend just kicked off its own
        // fire-and-forget refresh (routes/settings.ts) — see
        // pollForFreshCoverage's own doc comment for why this cannot be a
        // single immediate refetch instead.
        void pollForFreshCoverage(coverageBaseline);
        return true;
      } catch (err) {
        console.error('Size guide create error:', err);
        if (isLatestRequest(CREATE_KEY, seq)) {
          setSizeGuideError({ code: 'network_error' });
        }
        return false;
      } finally {
        // Clear busy only if the slot is still showing THIS create: a
        // patch or delete that started after this create must keep the
        // slot it already claimed.
        setSizeGuideBusy((current) =>
          current?.op === 'create' && isLatestRequest(CREATE_KEY, seq) ? null : current
        );
      }
    },
    [authenticatedFetch, beginRequest, isLatestRequest, pollForFreshCoverage]
  );

  /**
   * PATCH an existing guide's `name` and/or `categories`. The response body
   * IS the updated entry (routes/settings.ts PATCH /size-guides/:id) —
   * swapped into local state by id, never a refetch.
   *
   * A 409 (`settings_changed`) means the backend's own optimistic-concurrency
   * retry also lost a race against another edit. That code is surfaced like
   * any other rather than special-cased here: the section is the one that
   * tells the merchant to reload instead of retrying blind.
   *
   * Every write to the shared `sizeGuideBusy`/`sizeGuideError` slots is
   * guarded by `isLatestRequest`: a slower patch that a newer request for
   * this SAME guide has since superseded — another patch, or a delete —
   * must not clobber state that has already moved on, and must not steal
   * the busy/error slot back from whatever guide is now the most recent.
   * See `requestSeq`'s doc comment.
   */
  const updateSizeGuide = useCallback(
    async (id: string, patch: { name?: string; categories?: SizeGuideCategories }): Promise<boolean> => {
      const seq = beginRequest(id);
      setSizeGuideBusy({ op: 'patch', id });
      // Only THIS guide's previous error is this attempt's business —
      // another guide's card may be showing an error of its own right now.
      setSizeGuideError((current) => (current?.id === id ? null : current));
      // See createSizeGuide's identical snapshot for why.
      const coverageBaseline = settingsRef.current?.sizeCoverageReport?.checkedAt ?? null;

      try {
        const response = await authenticatedFetch(`/api/settings/size-guides/${id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        const json: SizeGuideResponse<SizeGuideEntryView> = await response.json();

        if (!response.ok || !json.success || !json.data) {
          if (isLatestRequest(id, seq)) {
            setSizeGuideError({ code: json.error || `http_${response.status}`, conflictName: json.conflictName, id });
          }
          return false;
        }

        const updated = json.data;
        setSettings((current) =>
          current
            ? { ...current, sizeGuides: current.sizeGuides.map((g) => (g.id === updated.id ? updated : g)) }
            : current
        );
        // A category change is exactly the kind of edit that can make a
        // guide stop fitting the products it is assigned to (or start
        // fitting ones it didn't before) — see pollForFreshCoverage.
        void pollForFreshCoverage(coverageBaseline);
        return true;
      } catch (err) {
        console.error('Size guide update error:', err);
        if (isLatestRequest(id, seq)) {
          setSizeGuideError({ code: 'network_error', id });
        }
        return false;
      } finally {
        // Clear busy only if the slot is still showing THIS guide's
        // mutation: a different guide's mutation that started after this
        // one must keep the slot it already claimed, and a NEWER request
        // for this same guide (id matches, but a fresher seq) must too.
        setSizeGuideBusy((current) => (current?.id === id && isLatestRequest(id, seq) ? null : current));
      }
    },
    [authenticatedFetch, beginRequest, isLatestRequest, pollForFreshCoverage]
  );

  /**
   * DELETE a guide by id. Nothing useful comes back to adopt, so on success
   * the entry is pulled out of local state directly, matching the backend's
   * own `$pull`.
   *
   * Same staleness guard as `updateSizeGuide` — see `requestSeq`'s doc
   * comment — and it shares that same per-id sequence counter, so a patch
   * and a delete racing for the SAME guide still resolve to whichever
   * actually started last, regardless of which kind of request it was.
   */
  const deleteSizeGuide = useCallback(
    async (id: string): Promise<boolean> => {
      const seq = beginRequest(id);
      setSizeGuideBusy({ op: 'delete', id });
      setSizeGuideError((current) => (current?.id === id ? null : current));
      // See createSizeGuide's identical snapshot for why.
      const coverageBaseline = settingsRef.current?.sizeCoverageReport?.checkedAt ?? null;

      try {
        const response = await authenticatedFetch(`/api/settings/size-guides/${id}`, { method: 'DELETE' });
        const json: SizeGuideResponse<never> = await response.json();

        if (!response.ok || !json.success) {
          if (isLatestRequest(id, seq)) {
            setSizeGuideError({ code: json.error || `http_${response.status}`, conflictName: json.conflictName, id });
          }
          return false;
        }

        setSettings((current) =>
          current ? { ...current, sizeGuides: current.sizeGuides.filter((g) => g.id !== id) } : current
        );
        // Removing a guide can uncover products it used to be the (right or
        // wrong) answer for — see pollForFreshCoverage.
        void pollForFreshCoverage(coverageBaseline);
        return true;
      } catch (err) {
        console.error('Size guide delete error:', err);
        if (isLatestRequest(id, seq)) {
          setSizeGuideError({ code: 'network_error', id });
        }
        return false;
      } finally {
        setSizeGuideBusy((current) => (current?.id === id && isLatestRequest(id, seq) ? null : current));
      }
    },
    [authenticatedFetch, beginRequest, isLatestRequest, pollForFreshCoverage]
  );

  /**
   * GET the shop's product types, for the admin's category suggestions.
   * GET /api/settings/product-types never fails on the backend — any trouble
   * there answers `{ success: true, data: { types: [] } }` — but a network
   * failure or a malformed response on this side is handled the same way:
   * an empty list, never a thrown error. There is no error state for this
   * call because an empty list already means "no suggestions available".
   */
  const fetchProductTypes = useCallback(async (): Promise<string[]> => {
    try {
      const response = await authenticatedFetch('/api/settings/product-types');
      const json: ApiResponse<{ types: string[] }> = await response.json();

      if (!response.ok || !json.success || !json.data) return [];
      return json.data.types;
    } catch (err) {
      console.error('Product types fetch error:', err);
      return [];
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    settings,
    loading,
    saving,
    error,
    updateSettings,
    refetch: fetchData,
    createSizeGuide,
    updateSizeGuide,
    deleteSizeGuide,
    fetchProductTypes,
    sizeGuideBusy,
    sizeGuideError,
  };
}
